import * as THREE from 'three';
import { TILE } from '../../shared/geo.js';

// Roads from the map's polylines, as ribbons hugging the ground, with the cross-section the city
// grammar asks for: a tarred road is a carriageway with laterite shoulders, an open concrete gutter
// on main roads and kerbs only on the biggest; an earth road is one ribbon of laterite. Shoulders
// and gutters go into the earth builder (one material, no extra draw calls) and sit a little lower
// than the tar, so at a junction the crossing road's carriageway covers them.

const WHITE = new THREE.Color(1, 1, 1);
const WATER = new THREE.Color('#3f7f8a');
const CONCRETE = new THREE.Color('#8a8a86'); // multiplies the laterite photo into grey gutter concrete
const KERB = new THREE.Color('#a8a8a4');
const STEP = 6; // metres between ribbon cross-sections

// BIM block (metres): shoulder, gutter and kerb sizes from the city grammar skill
const SHOULDER = 1.6;
const GUTTER = 0.5;
const KERB_W = 0.15;
const KERB_H = 0.12;

// The points of a polyline resampled every STEP metres, as tile-local metres.
function resample(p) {
  const xs = [];
  const zs = [];
  for (let i = 0; i < p.length - 2; i += 2) {
    const ax = p[i] / 10;
    const az = p[i + 1] / 10;
    const bx = p[i + 2] / 10;
    const bz = p[i + 3] / 10;
    const len = Math.hypot(bx - ax, bz - az);
    const parts = Math.max(1, Math.ceil(len / STEP));
    for (let k = 0; k < parts; k++) {
      xs.push(ax + ((bx - ax) * k) / parts);
      zs.push(az + ((bz - az) * k) / parts);
    }
  }
  xs.push(p[p.length - 2] / 10);
  zs.push(p[p.length - 1] / 10);
  return { xs, zs };
}

// Lays a strip along a resampled line between lateral offsets d0 and d1 (metres from the centre,
// left negative), each edge taking its own ground height plus `lift`. u runs u0..u1 across the
// strip, v along it at vScale per metre. `rise` lifts the right edge above the left (a gutter's dip).
function strip(builder, line, d0, d1, lift, tx, tz, heightmap, color, vScale, u0 = 0, u1 = 1, rise = 0) {
  const ox = tx * TILE;
  const oz = tz * TILE;
  const { xs, zs } = line;
  const n = xs.length;
  if (n < 2) return;
  let dist = 0;
  let prevL = null;
  let prevR = null;
  let prevV = 0;
  for (let i = 0; i < n; i++) {
    // direction averaged over the two neighbouring segments gives a mitred bend
    const i0 = Math.max(0, i - 1);
    const i1 = Math.min(n - 1, i + 1);
    let dx = xs[i1] - xs[i0];
    let dz = zs[i1] - zs[i0];
    const len = Math.hypot(dx, dz) || 1;
    dx /= len;
    dz /= len;
    // the left of the road as it runs: minus the normal (dz, -dx)
    const lx = xs[i] - dz * d0;
    const lz = zs[i] + dx * d0;
    const rx = xs[i] - dz * d1;
    const rz = zs[i] + dx * d1;
    const L = [lx, heightmap.heightAt(ox + lx, oz + lz) + lift, lz];
    const R = [rx, heightmap.heightAt(ox + rx, oz + rz) + lift + rise, rz];
    if (i > 0) dist += Math.hypot(xs[i] - xs[i - 1], zs[i] - zs[i - 1]);
    const v = dist * vScale;
    if (prevL) {
      builder.tri(prevL, prevR, R, color, [u0, prevV], [u1, prevV], [u1, v]);
      builder.tri(prevL, R, L, color, [u0, prevV], [u1, v], [u0, v]);
    }
    prevL = L;
    prevR = R;
    prevV = v;
  }
}

// A small vertical face along the line at lateral offset d, from lift y0 up to y1 (a kerb's side).
function wall(builder, line, d, y0, y1, tx, tz, heightmap, color, facingOut) {
  const ox = tx * TILE;
  const oz = tz * TILE;
  const { xs, zs } = line;
  const n = xs.length;
  if (n < 2) return;
  let prev = null;
  const uv = [0.5, 0.5];
  for (let i = 0; i < n; i++) {
    const i0 = Math.max(0, i - 1);
    const i1 = Math.min(n - 1, i + 1);
    let dx = xs[i1] - xs[i0];
    let dz = zs[i1] - zs[i0];
    const len = Math.hypot(dx, dz) || 1;
    dx /= len;
    dz /= len;
    const x = xs[i] - dz * d;
    const z = zs[i] + dx * d;
    const g = heightmap.heightAt(ox + x, oz + z);
    const cur = [[x, g + y0, z], [x, g + y1, z]];
    if (prev) {
      if (facingOut) {
        builder.tri(prev[0], cur[0], cur[1], color, uv, uv, uv);
        builder.tri(prev[0], cur[1], prev[1], color, uv, uv, uv);
      } else {
        builder.tri(cur[0], prev[0], prev[1], color, uv, uv, uv);
        builder.tri(cur[0], prev[1], cur[1], color, uv, uv, uv);
      }
    }
    prev = cur;
  }
}

// Adds a tile's roads to the tar and earth builders, with their cross-sections.
export function addRoads(data, tx, tz, heightmap, tar, earth) {
  for (const r of data.r) {
    if (r.p.length < 4) continue;
    const line = resample(r.p);
    const half = r.w / 2;
    if (r.s === 1) {
      // laterite: one ribbon, the wheel ruts in its texture
      strip(earth, line, -half, half, 0.07 + (7 - r.c) * 0.004, tx, tz, heightmap, WHITE, 1 / 6);
      continue;
    }
    const lift = 0.12 + (8 - r.c) * 0.004;
    // markings only on main roads: the left half of the tar texture carries them, the right is plain
    const marked = r.c <= 3;
    strip(tar, line, -half, half, lift, tx, tz, heightmap, WHITE, 1 / 14, marked ? 0 : 0.5, marked ? 0.5 : 1);
    let edge = half;
    if (r.c <= 1) {
      // trunk and primary roads in town: a kerb either side
      wall(earth, line, half, lift, lift + KERB_H, tx, tz, heightmap, KERB, true);
      wall(earth, line, -half, lift, lift + KERB_H, tx, tz, heightmap, KERB, false);
      strip(earth, line, half, half + KERB_W, lift + KERB_H, tx, tz, heightmap, KERB, 1 / 6, 0.45, 0.55);
      strip(earth, line, -half - KERB_W, -half, lift + KERB_H, tx, tz, heightmap, KERB, 1 / 6, 0.45, 0.55);
      edge = half + KERB_W;
    }
    // laterite shoulders, a little below the tar
    strip(earth, line, edge, edge + SHOULDER, 0.06, tx, tz, heightmap, WHITE, 1 / 6);
    strip(earth, line, -edge - SHOULDER, -edge, 0.06, tx, tz, heightmap, WHITE, 1 / 6);
    if (r.c <= 3) {
      // the open concrete gutter beyond the shoulder, dipping away from the road
      const g0 = edge + SHOULDER;
      strip(earth, line, g0, g0 + GUTTER, 0.03, tx, tz, heightmap, CONCRETE, 1 / 6, 0.45, 0.55, -0.12);
      strip(earth, line, -g0 - GUTTER, -g0, 0.03 - 0.12, tx, tz, heightmap, CONCRETE, 1 / 6, 0.45, 0.55, 0.12);
    }
  }
}

// Adds a tile's rivers, streams and lakes to the water builder.
export function addWater(data, tx, tz, heightmap, water) {
  const ox = tx * TILE;
  const oz = tz * TILE;
  for (const w of data.w) {
    if (w.p.length >= 4) strip(water, resample(w.p), -w.w / 2, w.w / 2, 0.05, tx, tz, heightmap, WATER, 1 / 8);
  }
  for (const a of data.a) {
    const n = a.p.length / 2;
    if (n < 3) continue;
    const contour = [];
    let level = Infinity;
    for (let i = 0; i < n; i++) {
      const x = a.p[i * 2] / 10;
      const z = a.p[i * 2 + 1] / 10;
      contour.push(new THREE.Vector2(x, z));
      level = Math.min(level, heightmap.heightAt(ox + x, oz + z));
    }
    // a lake is level: sit just above its lowest bank
    const y = level + 0.35;
    for (const f of THREE.ShapeUtils.triangulateShape(contour, [])) {
      const A = [contour[f[0]].x, y, contour[f[0]].y];
      const B = [contour[f[1]].x, y, contour[f[1]].y];
      const C = [contour[f[2]].x, y, contour[f[2]].y];
      const up = (B[2] - A[2]) * (C[0] - A[0]) - (B[0] - A[0]) * (C[2] - A[2]) >= 0;
      if (up) water.tri(A, B, C, WATER, [A[0] / 8, A[2] / 8], [B[0] / 8, B[2] / 8], [C[0] / 8, C[2] / 8]);
      else water.tri(A, C, B, WATER, [A[0] / 8, A[2] / 8], [C[0] / 8, C[2] / 8], [B[0] / 8, B[2] / 8]);
    }
  }
}
